import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Building2, Users, Package } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { AccountService } from '@/lib/profile-service';

interface Account {
  id: string;
  user_type?: 'buyer' | 'supplier' | 'both';
  full_name?: string;
  company_name?: string;
}

const DashboardSelector = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAccount = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const accountService = new AccountService();
        const accountData = await accountService.getAccount(user.id);
        
        if (!accountData) {
          navigate('/account-setup');
          return;
        }

        // Check if required fields are missing
        if (!accountData.user_type || !accountData.full_name) {
          navigate('/account-setup');
          return;
        }

        setAccount(accountData);

        // If user is not 'both' type, redirect them to their appropriate dashboard
        if (accountData.user_type === 'buyer') {
          navigate('/buyer-dashboard');
          return;
        } else if (accountData.user_type === 'supplier') {
          navigate('/supplier-dashboard');
          return;
        }
        
        // If user_type is 'both', show the selector
        setLoading(false);
      } catch (error) {
        console.error('Error loading account:', error);
        navigate('/account-setup');
      }
    };

    loadAccount();
  }, [user, navigate]);

  const handleBuyerDashboard = () => {
    navigate('/buyer-dashboard');
  };

  const handleSupplierDashboard = () => {
    navigate('/supplier-dashboard');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome, {account?.full_name || 'User'}!</h1>
          <p className="text-muted-foreground mb-4">
            You have access to both buyer and supplier features. Choose which dashboard you'd like to access:
          </p>
          <Badge variant="secondary" className="text-sm">
            Account Type: Buyer & Supplier
          </Badge>
        </div>

        {/* Dashboard Options */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Buyer Dashboard */}
          <Card className="cursor-pointer transition-all hover:shadow-lg border-2 hover:border-primary">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-xl">Buyer Dashboard</CardTitle>
              <CardDescription>
                Access buying features, manage inquiries, and track orders
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>Send inquiries to suppliers</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Package className="w-4 h-4" />
                  <span>Request quotes for products</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ShoppingCart className="w-4 h-4" />
                  <span>Manage orders and purchases</span>
                </div>
              </div>
              <Button 
                onClick={handleBuyerDashboard}
                className="w-full"
                variant="outline"
              >
                Access Buyer Dashboard
              </Button>
            </CardContent>
          </Card>

          {/* Supplier Dashboard */}
          <Card className="cursor-pointer transition-all hover:shadow-lg border-2 hover:border-primary">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Building2 className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-xl">Supplier Dashboard</CardTitle>
              <CardDescription>
                Manage your business, products, and customer relationships
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Package className="w-4 h-4" />
                  <span>Add and manage products</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>Respond to buyer inquiries</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="w-4 h-4" />
                  <span>Manage business profile</span>
                </div>
              </div>
              <Button 
                onClick={handleSupplierDashboard}
                className="w-full"
              >
                Access Supplier Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Additional Options */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            You can switch between dashboards anytime from your profile settings.
          </p>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/account')}
            className="text-sm"
          >
            Manage Account Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DashboardSelector; 